/* eslint-disable react/prop-types */
import {
  Chart,
  ChartLegend,
  ChartSeries,
  ChartSeriesItem,
  ChartSeriesItemTooltip,
  ChartTooltip,
  ChartXAxis,
  ChartXAxisItem,
  ChartYAxis,
  ChartYAxisItem,
} from '@progress/kendo-react-charts';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  dateAdd,
  formatDate,
  formatPrice,
} from '../../../../../../common/global/helpers';
import useMount from '../../../../../../common/hooks/useMount';
import { ProductTypes } from '../../../../enums';
import { RevenueItemModel } from '../../../../models/analytics';
import { DropdownOptionModel } from '../../../../models/common';
import { resources } from '../../../../resources/resources';
import { analyticsService } from '../../../../services/analytics';
import { PeriodSelector } from '../PeriodSelector';
import { ProductSelector } from '../ProductSelector';
import { PeriodModel } from '../types';
import {
  getChartPeriods,
  getLegendItemVisual,
  getPreviousPeriod,
  mapPeriod,
  markerTooltipColor,
  markerTooltipStyle,
  seriesColor,
  seriesPeriods,
} from '../utils';

interface RevenueChartProps {
  setPeriod?: (period: PeriodModel) => void;
}

export const RevenueChart = (props: RevenueChartProps) => {
  const { t: translate } = useTranslation();
  const [data, setData] = useState<RevenueItemModel[]>([]);
  const [previousData, setPreviousData] = useState<RevenueItemModel[]>([]);
  const [period, setPeriod] = useState<PeriodModel>(
    mapPeriod(getChartPeriods(translate)[0]),
  );
  const [hasPayableProducts, setHasPayableProducts] = useState<boolean>(false);
  const [productId, setProductId] = useState<number | undefined>();
  const [products, setProducts] = useState<DropdownOptionModel[]>([]);

  const loadProducts = async (abortController?: AbortController) => {
    const resp = await analyticsService.getProducts(
      [ProductTypes.ProductOrService, ProductTypes.GiftCard],
      {
        signal: abortController?.signal,
      },
    );
    if (abortController?.signal.aborted) return;
    if (!resp.hasErrors) {
      setProducts(resp.data);
    }
  };

  const loadData = async (
    period: PeriodModel,
    productId?: number,
    abortController?: AbortController,
  ) => {
    const resp = await analyticsService.getRevenue(
      period.startDate,
      period.endDate,
      productId,
      {
        signal: abortController?.signal,
      },
    );
    if (abortController?.signal.aborted) return;
    if (!resp.hasErrors) {
      setData(resp.data.map(e => ({ ...e, date: new Date(e.date) })));
    }
  };

  const loadHasPayableProducts = async (abortController?: AbortController) => {
    const resp = await analyticsService.getHasPayableProducts({
      signal: abortController?.signal,
    });
    if (abortController?.signal.aborted) return;
    if (!resp.hasErrors) {
      setHasPayableProducts(resp.data);
    }
  };

  const loadPreviousData = async (
    period: PeriodModel,
    productId?: number,
    abortController?: AbortController,
  ) => {
    const previousPeriod = getPreviousPeriod(period);
    const resp = await analyticsService.getRevenue(
      previousPeriod.startDate,
      previousPeriod.endDate,
      productId,
      {
        signal: abortController?.signal,
      },
    );
    if (abortController?.signal.aborted) return;
    if (!resp.hasErrors) {
      setPreviousData(resp.data.map(e => ({ ...e, date: new Date(e.date) })));
    }
  };

  useMount(abortController => {
    loadData(period, productId, abortController);
    loadPreviousData(period, productId, abortController);
    loadHasPayableProducts(abortController);
    loadProducts(abortController);
  });

  useEffect(() => {
    loadData(period, productId);
    loadPreviousData(period, productId);
  }, [period, productId]);

  if (!hasPayableProducts) return null;

  return (
    <div className="m-panel m-analytics-panel">
      <div className="m-page-actions">
        <h2>{translate(resources.analytics.charts.totalRevenue)}</h2>
        <div className="m-analytics-filters">
          <div className="m-page-action">
            <PeriodSelector
              period={period}
              setPeriod={period => {
                setData([]);
                setPeriod(period);
                props.setPeriod?.(period);
              }}
            />
          </div>
          <div className="m-page-action">
            <ProductSelector
              data={products}
              productId={productId}
              setProductId={setProductId}
            />
          </div>
        </div>
      </div>

      <Chart transitions={false}>
        <ChartTooltip />
        <ChartLegend
          position="bottom"
          item={{
            visual: props => getLegendItemVisual(props, period),
          }}
        />
        <ChartSeries>
          <ChartSeriesItem
            type="scatterLine"
            yField="amount"
            xField="date"
            data={previousData.map(e => ({
              ...e,
              date: dateAdd(new Date(e.date), { years: 1 }),
            }))}
            name={seriesPeriods.previous}
            color={seriesColor}
            opacity={0.5}
            dashType="dot"
            width={2}
          >
            <ChartSeriesItemTooltip
              background={markerTooltipColor}
              render={({ point }) => {
                return (
                  <div style={markerTooltipStyle}>
                    <b>
                      {formatDate(
                        dateAdd(point.value.x, { years: -1 }).toString(),
                      )}
                    </b>
                    <b>{`${formatPrice(point.value.y)} RON`}</b>
                  </div>
                );
              }}
            />
          </ChartSeriesItem>
          <ChartSeriesItem
            type="scatterLine"
            yField="amount"
            xField="date"
            data={data}
            name={seriesPeriods.current}
            color={seriesColor}
            markers={{ background: seriesColor }}
            opacity={1}
            width={2}
          >
            <ChartSeriesItemTooltip
              background={markerTooltipColor}
              render={({ point }) => {
                return (
                  <div style={markerTooltipStyle}>
                    <b>{formatDate(point.value.x.toString())}</b>
                    <b>{`${formatPrice(point.value.y)} RON`}</b>
                  </div>
                );
              }}
            />
          </ChartSeriesItem>
        </ChartSeries>
        <ChartXAxis>
          <ChartXAxisItem
            baseUnit="days"
            majorGridLines={{ visible: false }}
            labels={{
              content: e => formatDate(e.value.toString(), 'DD MMM'),
            }}
          />
        </ChartXAxis>
        <ChartYAxis>
          <ChartYAxisItem labels={{ content: e => `${e.value} RON` }} />
        </ChartYAxis>
      </Chart>
    </div>
  );
};
